import type { QuantityOfferConfig, QuantityOfferTier } from '@cod/config-schema';

/**
 * The Quantity Offers pages' pure model.
 *
 * Separate from `QuantityOfferEditor.tsx` because that module pulls in Nimbus and the Nexo-backed
 * api client, which need a `window`. Keeping the rules here makes them testable in a node
 * environment.
 */

/**
 * Apply a preselect toggle across a tier list, keeping preselection **exclusive**.
 *
 * The storefront highlights the FIRST tier with `preselect` (`initialTier` in
 * `@cod/form-tree/pure` uses `find`), so a config with two checked tiers shows the merchant two
 * preselected offers while buyers only ever get one. Checking a tier therefore clears the others.
 *
 * Unchecking only touches its own tier: clearing siblings there would silently drop a
 * preselection the merchant never pointed at (and it can only exist in configs saved before this
 * rule, since checking now enforces it).
 */
export function applyPreselect(
  tiers: QuantityOfferTier[],
  id: string,
  preselect: boolean,
): QuantityOfferTier[] {
  return tiers.map((tier) => {
    if (tier.id === id) return { ...tier, preselect };
    return preselect ? { ...tier, preselect: false } : tier;
  });
}

/**
 * Names of products targeted by more than one of the given offers.
 *
 * Only one offer can apply per product on the storefront (the first enabled match wins), so an
 * overlap silently drops the lower-priority offer. Callers decide which offers to pass: the Offers
 * page includes disabled ones (it is editing them), the dashboard warns about enabled ones only.
 */
export function overlappingProductNames(offers: QuantityOfferConfig[]): string[] {
  const byProduct = new Map<number, { name: string; count: number }>();
  for (const offer of offers) {
    for (const product of offer.products) {
      const entry = byProduct.get(product.id) ?? { name: product.name, count: 0 };
      byProduct.set(product.id, { name: product.name, count: entry.count + 1 });
    }
  }
  return [...byProduct.values()].filter((e) => e.count > 1).map((e) => e.name);
}

/**
 * An offer name with its `Copy(n)` suffix stripped — the name a copy was made FROM.
 *
 * `copyLabel` is passed in because it is translated (`quantityOffers.copyLabel`: Copy / Copia),
 * and it is regex-escaped here so a label with punctuation cannot corrupt the pattern.
 *
 * Only ONE suffix is stripped, and only from the end: a copy of a copy is named from the same
 * base (`X Copy(1)` → `X Copy(2)`, never `X Copy(1) Copy(1)`), which is what keeps every copy of
 * an offer in one series.
 */
export function copyBaseName(name: string, copyLabel: string): string {
  const escaped = copyLabel.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return name.replace(new RegExp(`\\s*${escaped}\\(\\d+\\)$`), '').trimEnd() || name;
}

/**
 * Where a new copy of `offers[sourceIndex]` belongs: **after the source and after the copies it
 * already has**, so the series reads original, Copy(1), Copy(2) top to bottom.
 *
 * 🚨 Inserting at `sourceIndex + 1` — the obvious version — puts each new copy directly under the
 * original and therefore ABOVE its older siblings, so duplicating twice listed Copy(2) before
 * Copy(1). The list order IS the offer priority (first enabled match wins per product), so that
 * inverted a merchant's ranking every time they made a second copy.
 *
 * The scan is deliberately **contiguous**: it stops at the first row whose base name differs
 * instead of hunting to the end of the list. A copy that has been dragged elsewhere, or renamed
 * to something of its own, is no longer part of the block — jumping over unrelated offers to sit
 * beside it would move the new copy past priorities the merchant set by hand.
 */
export function duplicateInsertIndex(
  offers: readonly QuantityOfferConfig[],
  sourceIndex: number,
  copyLabel: string,
): number {
  const source = offers[sourceIndex];
  if (!source) return offers.length;
  const base = copyBaseName(source.name, copyLabel);
  let at = sourceIndex + 1;
  while (at < offers.length && copyBaseName(offers[at]!.name, copyLabel) === base) at += 1;
  return at;
}

/** What the Quantity Offers list is showing instead of — or as — its rows. */
export type OffersListView = 'onboarding' | 'removed' | 'noResults' | 'rows';

/**
 * Pick the list page's state.
 *
 * 🚨 This exists as a function, rather than an inline `offers.length === 0`, because **deleting is
 * staged**. Deleting the last offer empties the working list while the Save/Discard pair is the
 * only thing that can resolve it — so treating "no rows" as the onboarding empty state hid that
 * pair behind the very card that replaced it. What the merchant was left with was an Add offer
 * button that the navigation guard then blocked, prompting "unsaved changes" for edits they could
 * neither see nor save: "Keep editing" returned them to a screen with nothing to edit, and
 * "Discard changes" brought every deleted offer back. `removed` is that state, and it keeps the
 * toolbar.
 *
 * `onboarding` deliberately ignores the search query: with nothing staged and no offers, the store
 * has none at all, and "no offers found with that name" is a worse answer than "create your first".
 */
export function offersListView(args: {
  /** Offers in the working list — staged edits applied, search NOT applied. */
  offers: number;
  /** How many of those match the current search. */
  matches: number;
  /** Whether staged edits are waiting on Save/Discard. */
  dirty: boolean;
}): OffersListView {
  if (args.offers === 0) return args.dirty ? 'removed' : 'onboarding';
  return args.matches === 0 ? 'noResults' : 'rows';
}
